import { Row, Col, Card, Skeleton, Avatar, Image, Tabs, Tag, Table, Alert, Empty, Button, Badge, Collapse } from "antd";
import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import apiPath from "../../constants/apiPath";
import { formatDate, formatDateTime } from "../../helper/DateHelper";
import {
  HeartFilled,
  InboxOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ShoppingOutlined,
  ArrowLeftOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  SafetyOutlined,
  LinkOutlined,
  PictureOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import './View.css';

function View() {

  const sectionName = "User";
  const routeName = "user";

  const params = useParams();
  const location = useLocation();
  const { request } = useRequest();
  const [list, setList] = useState({});
  const [wishlist, setWishlist] = useState([]);
  const [userBrands, setUserBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [activeBrandTab, setActiveBrandTab] = useState(null);

  const fetchData = (id, otpVerification) => {
    setLoading(true);
    request({
      url: apiPath.viewUser + "/" + id + `?otp_verification=${otpVerification}`,
      method: 'GET',
      onSuccess: (response) => {
        setLoading(false);
        setList(response.data.userInfo);
        setWishlist(response.data.wishlist);
        setUserBrands(response.data.userBrands || []);
      },
      onError: (error) => {
        ShowToast(error, Severty.ERROR)
      }
    })
  }

  const handleRefresh = () => {
    const searchParams = new URLSearchParams(location.search);
    const otpVerification = searchParams.get('tab');
    fetchData(params.id, otpVerification);
  }

  const findUserBrandByServiceId = (serviceId) => {
    return userBrands.find(brand => brand?.brand_id?._id === serviceId);
  };

  const handleFavouriteBrandClick = (serviceId) => {
    const userBrand = findUserBrandByServiceId(serviceId);
    if (userBrand) {
      setActiveBrandTab(userBrand._id);
      setActiveTab('brands');
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const otpVerification = searchParams.get('tab');
    fetchData(params.id, otpVerification)
  }, [])

  const tabItems = [
    {
      key: 'profile',
      label: (
        <span>
          <UserOutlined />
          User Profile
        </span>
      ),
      children: renderUserProfile()
    },
    {
      key: 'wishlists',
      label: (
        <span>
          <HeartFilled />
          Favourites
        </span>
      ),
      children: renderWishlists()
    },
    {
      key: 'brands',
      label: (
        <span>
          <ShoppingOutlined />
          User Brands
        </span>
      ),
      children: renderUserBrands()
    }
  ];

  function renderUserProfile() {
    return (
      <>
        {loading ? (
          <div>
            {[1, 2, 3].map(item => <Skeleton active key={item} style={{ marginBottom: '20px' }} />)}
          </div>
        ) : (
          <div className="user-profile-section">
            <Row gutter={[24, 24]}>
              {/* Profile Header Card */}
              <Col xs={24} md={8}>
                <Card className="profile-header-card" bordered={false}>
                  <div className="profile-avatar-wrapper">
                    <div className="profile-avatar-ring">
                      {list?.image ? (
                        <Image
                          src={apiPath.assetURL + list.image}
                          width={120}
                          height={120}
                          style={{ borderRadius: '50%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Avatar size={120}>
                          {list?.name?.charAt(0)?.toUpperCase() ?? "U"}
                        </Avatar>
                      )}
                    </div>
                    <h2 className="profile-name">{list?.name ?? 'Anonymous User'}</h2>
                    <p className="profile-role">{list?.user_type ?? 'User'}</p>
                    <div className="profile-badges">
                      {list?.is_active ? (
                        <Tag color="success" icon={<CheckCircleOutlined />}>Active</Tag>
                      ) : (
                        <Tag color="error" icon={<ClockCircleOutlined />}>Inactive</Tag>
                      )}
                      {list?.is_otp_verify ? (
                        <Tag color="processing">OTP Verified</Tag>
                      ) : (
                        <Tag color="warning">OTP Pending</Tag>
                      )}
                    </div>
                  </div>
                </Card>
              </Col>

              {/* Info Cards */}
              <Col xs={24} md={16}>
                <Row gutter={[20, 20]}>
                  {/* Basic Information */}
                  <Col xs={24}>
                    <Card
                      className="info-card"
                      title={<><IdcardOutlined /> Basic Information</>}
                    >
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                          <div className="info-grid-item">
                            <div className="info-grid-icon">
                              <UserOutlined />
                            </div>
                            <div className="info-grid-content">
                              <p className="info-grid-label">First Name</p>
                              <p className="info-grid-value">{list?.first_name || '-'}</p>
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <div className="info-grid-item">
                            <div className="info-grid-icon">
                              <UserOutlined />
                            </div>
                            <div className="info-grid-content">
                              <p className="info-grid-label">Middle Name</p>
                              <p className="info-grid-value">{list?.middle_name || '-'}</p>
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <div className="info-grid-item">
                            <div className="info-grid-icon">
                              <UserOutlined />
                            </div>
                            <div className="info-grid-content">
                              <p className="info-grid-label">Last Name</p>
                              <p className="info-grid-value">{list?.last_name || '-'}</p>
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <div className="info-grid-item">
                            <div className="info-grid-icon">
                              <CalendarOutlined />
                            </div>
                            <div className="info-grid-content">
                              <p className="info-grid-label">Date of Birth</p>
                              <p className="info-grid-value">{list?.dob || '-'}</p>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  </Col>

                  {/* Contact Information */}
                  <Col xs={24}>
                    <Card
                      className="info-card"
                      title={<><PhoneOutlined /> Contact Information</>}
                    >
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <div className="info-grid-item">
                            <div className="info-grid-icon">
                              <MailOutlined />
                            </div>
                            <div className="info-grid-content">
                              <p className="info-grid-label">Email Address</p>
                              <p className="info-grid-value">{list?.email || '-'}</p>
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div className="info-grid-item">
                            <div className="info-grid-icon">
                              <PhoneOutlined />
                            </div>
                            <div className="info-grid-content">
                              <p className="info-grid-label">Phone Number</p>
                              <p className="info-grid-value">
                                {list?.country_code ? `+${list.country_code} ` : ''}{list?.mobile_number || '-'}
                              </p>
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div className="info-grid-item">
                            <div className="info-grid-icon">
                              <EnvironmentOutlined />
                            </div>
                            <div className="info-grid-content">
                              <p className="info-grid-label">City</p>
                              <p className="info-grid-value">{list?.city || '-'}</p>
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div className="info-grid-item">
                            <div className="info-grid-icon">
                              <EnvironmentOutlined />
                            </div>
                            <div className="info-grid-content">
                              <p className="info-grid-label">Address</p>
                              <p className="info-grid-value">{list?.address || '-'}</p>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  </Col>

                  {/* Security & Timeline */}
                  <Col xs={24} lg={12}>
                    <Card
                      className="info-card"
                      title={<><SafetyOutlined /> Security</>}
                    >
                      <Row gutter={[16, 16]}>
                        {list?.otp && (
                          <Col xs={24}>
                            <div className="info-grid-item">
                              <div className="info-grid-icon">
                                <SafetyOutlined />
                              </div>
                              <div className="info-grid-content">
                                <p className="info-grid-label">OTP Code</p>
                                <p className="info-grid-value">{list.otp}</p>
                              </div>
                            </div>
                          </Col>
                        )}
                        {list?.otp_verify_at && (
                          <Col xs={24}>
                            <div className="info-grid-item">
                              <div className="info-grid-icon">
                                <CheckCircleOutlined />
                              </div>
                              <div className="info-grid-content">
                                <p className="info-grid-label">OTP Verified On</p>
                                <p className="info-grid-value">{formatDateTime(list.otp_verify_at)}</p>
                              </div>
                            </div>
                          </Col>
                        )}
                        {!list?.otp && !list?.otp_verify_at && (
                          <Col xs={24}>
                            <Empty description="No security info available" className="empty-state" />
                          </Col>
                        )}
                      </Row>
                    </Card>
                  </Col>

                  <Col xs={24} lg={12}>
                    <Card
                      className="info-card"
                      title={<><CalendarOutlined /> Timeline</>}
                    >
                      <Row gutter={[16, 16]}>
                        <Col xs={24}>
                          <div className="info-grid-item">
                            <div className="info-grid-icon">
                              <CalendarOutlined />
                            </div>
                            <div className="info-grid-content">
                              <p className="info-grid-label">Member Since</p>
                              <p className="info-grid-value">{formatDate(list?.created_at)}</p>
                            </div>
                          </div>
                        </Col>
                        <Col xs={24}>
                          <div className="info-grid-item">
                            <div className="info-grid-icon">
                              <ClockCircleOutlined />
                            </div>
                            <div className="info-grid-content">
                              <p className="info-grid-label">Last Updated</p>
                              <p className="info-grid-value">{formatDateTime(list?.updated_at)}</p>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        )}
      </>
    );
  }

  function renderWishlists() {
    const activeCount = wishlist?.active?.length || 0;
    const archivedCount = wishlist?.archived?.length || 0;

    return (
      <>
        {loading ? (
          <div>
            {[1, 2].map(item => <Skeleton active key={item} style={{ marginBottom: '20px' }} />)}
          </div>
        ) : (
          <div className="wishlists-section">
            <Row gutter={[24, 24]}>
              {/* Active Favourites */}
              <Col xs={24} lg={12}>
                <Card
                  className="wishlist-card-modern active-card"
                  title={
                    <div className="wishlist-card-header">
                      <HeartFilled />
                      <span>Active Favourite Brands ({activeCount})</span>
                    </div>
                  }
                >
                  {activeCount > 0 ? (
                    <div className="wishlist-list">
                      {wishlist.active.map((item, index) => {
                        const hasUserBrand = findUserBrandByServiceId(item?.service_id?._id);
                        return (
                          <div
                            key={item._id || index}
                            className={`wishlist-list-item active-item ${hasUserBrand ? 'clickable' : ''}`}
                            onClick={() => hasUserBrand && handleFavouriteBrandClick(item?.service_id?._id)}
                            style={hasUserBrand ? { cursor: 'pointer' } : {}}
                          >
                            <div className="wishlist-number">{index + 1}</div>
                            <div className="wishlist-info">
                              <p className="wishlist-name">
                                {item?.service_id?.name || 'Unknown Service'}
                                {hasUserBrand && <Tag color="purple" style={{ marginLeft: 8 }}>Click to View Data</Tag>}
                              </p>
                              <p className="wishlist-date">
                                <CalendarOutlined /> Added: {formatDate(item.created_at)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty description="No active favourites brands" className="empty-state" />
                  )}
                </Card>
              </Col>

              {/* Archived Favourites */}
              <Col xs={24} lg={12}>
                <Card
                  className="wishlist-card-modern archived-card"
                  title={
                    <div className="wishlist-card-header">
                      <InboxOutlined />
                      <span>Archived Favourite Brands ({archivedCount})</span>
                    </div>
                  }
                >
                  {archivedCount > 0 ? (
                    <div className="wishlist-list">
                      {wishlist.archived.map((item, index) => {
                        const hasUserBrand = findUserBrandByServiceId(item?.service_id?._id);
                        return (
                          <div
                            key={item._id || index}
                            className={`wishlist-list-item archived-item ${hasUserBrand ? 'clickable' : ''}`}
                            onClick={() => hasUserBrand && handleFavouriteBrandClick(item?.service_id?._id)}
                            style={hasUserBrand ? { cursor: 'pointer' } : {}}
                          >
                            <div className="wishlist-number">{index + 1}</div>
                            <div className="wishlist-info">
                              <p className="wishlist-name">
                                {item?.service_id?.name || 'Unknown Service'}
                                {hasUserBrand && <Tag color="purple" style={{ marginLeft: 8 }}>Click to View Data</Tag>}
                              </p>
                              <p className="wishlist-date">
                                <CalendarOutlined /> Archived: {formatDate(item.updated_at)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty description="No archived favourites brands" className="empty-state" />
                  )}
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </>
    );
  }

  function renderUserBrands() {

    const renderServiceDetails = (ps, index) => (
      <Card
        key={ps._id || index}
        className={`service-details-card ${ps?.deleted_at ? 'deleted-service' : ''}`}
        title={
          <div className="service-details-header">
            <span className="service-details-name">{ps?.ps_name}</span>
            <Tag color="blue"><PictureOutlined /> {ps.images?.length || 0} Images</Tag>
            {ps?.deleted_at && (
              <Tag color="error">DELETED - {formatDateTime(ps.deleted_at)}</Tag>
            )}
          </div>
        }
      >
        {ps?.images?.length > 0 && (
          <div className="service-images-grid">
            {ps.images.map((img, idx) => (
              <div key={idx} className="service-image-item">
                <Image src={img} alt={`Image ${idx + 1}`} />
              </div>
            ))}
          </div>
        )}

        {ps?.psla_list?.length > 0 ? (
          <Table
            className="attributes-table"
            size="small"
            bordered
            pagination={false}
            dataSource={[
              ...ps.psla_list.map((item, idx) => ({ ...item, serial_no: idx + 1 })),
              ...(ps?.remark ? [{ isRemark: true, remark: ps.remark }] : [])
            ]}
            rowKey={(r, i) => i}
            columns={[
              {
                title: "Sr. No.",
                dataIndex: "serial_no",
                width: 80,
                render: (val, record) => record.isRemark ? { children: <><strong>Remark:</strong> {record.remark}</>, props: { colSpan: 4 } } : val
              },
              {
                title: "Label",
                dataIndex: "psla_label",
                render: (val, record) => record.isRemark ? { props: { colSpan: 0 } } : val
              },
              {
                title: "Value",
                dataIndex: "value",
                render: (val, record) => record.isRemark ? { props: { colSpan: 0 } } : (val || '-')
              },
              {
                title: "Required",
                width: 100,
                render: (val, record) => record.isRemark
                  ? { props: { colSpan: 0 } }
                  : (val?.psla_required === "Yes" ? <Tag color="error">Yes</Tag> : <Tag color="default">No</Tag>)
              }
            ]}
          />
        ) : (
          <Empty description="No attributes available" className="empty-state" />
        )}
      </Card>
    );

    const renderServiceLayer = (psl, idx) => (
      <Collapse.Panel
        key={psl._id || idx}
        header={
          <div className="service-layer-header">
            <span className="service-layer-name">{psl?.psl_name || `Service Layer ${idx + 1}`}</span>
            <Badge count={psl?.ps_list?.length || 0} style={{ backgroundColor: '#667eea' }} />
          </div>
        }
      >
        {psl?.ps_list?.length > 0 ? (
          psl.ps_list.map((ps, index) => renderServiceDetails(ps, index))
        ) : (
          <Empty description="No services available" className="empty-state" />
        )}
      </Collapse.Panel>
    );

    const getBrandWishlistStatus = (brandId) => {
      const isActive = wishlist?.active?.some(item => item?.service_id?._id === brandId);
      const isArchived = wishlist?.archived?.some(item => item?.service_id?._id === brandId);
      return { isActive, isArchived };
    };

    const renderWishlistIndicator = (brandId, showText = true) => {
      const { isActive, isArchived } = getBrandWishlistStatus(brandId);

      if (isActive) {
        return showText
          ? <Tag color="red" icon={<HeartFilled style={{ color: '#ff4d4f' }} />}>Favourite</Tag>
          : <HeartFilled style={{ color: '#ff4d4f', fontSize: '16px' }} />;
      }
      if (isArchived) {
        return showText
          ? <Tag color="default" icon={<InboxOutlined />}>Archived</Tag>
          : <InboxOutlined style={{ color: '#4facfe', fontSize: '16px' }} />;
      }
      return null;
    };

    const renderLocationDisplay = (brand) => {
      const citySelection = brand?.brand_id?.city_selection;

      if (citySelection === "All Cities") {
        return (
          <div className="location-display">
            <span className="location-tag all-cities">
              <EnvironmentOutlined /> All Cities
            </span>
          </div>
        );
      }

      if (citySelection === "Single City") {
        const cityName = brand?.brand_id?.city_id?.name;
        return (
          <div className="location-display">
            <span className="location-tag single-city">
              <EnvironmentOutlined /> Single City
            </span>
            {cityName && <span className="location-cities">{cityName}</span>}
          </div>
        );
      }

      if (citySelection === "Multiple Cities") {
        const cities = brand?.brand_id?.multiple_cities;
        const cityNames = Array.isArray(cities)
          ? cities.map(c => c?.name).filter(Boolean).join(', ')
          : '';
        return (
          <div className="location-display">
            <span className="location-tag multiple-cities">
              <EnvironmentOutlined /> Multiple Cities
            </span>
            {cityNames && <span className="location-cities">{cityNames}</span>}
          </div>
        );
      }

      return <span>-</span>;
    };

    const renderBrandContent = (brand) => (
      <div className="brands-section">
        <Row gutter={[20, 20]}>
          <Col xs={24} lg={24}>
            <Card className="brand-info-card" title=
              {
                <>
                  Brand Information 
                  <Badge count={brand?.psl_list?.length || 0} 
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', marginLeft: "5px", marginRight: "10px" }}
                  />
                </>
              }>
              <div className="brand-info-grid">
                <div className="brand-info-item">
                  <div className="brand-info-label">Brand Code</div>
                  <div className="brand-info-value">{brand?.brand_id?.sp_code || '-'}</div>
                </div>
                <div className="brand-info-item">
                  <div className="brand-info-label">Location</div>
                  <div className="brand-info-value">
                    {renderLocationDisplay(brand)}
                  </div>
                </div>
                <div className="brand-info-item">
                  <div className="brand-info-label">Status</div>
                  <div className="brand-info-value">
                    {renderWishlistIndicator(brand?.brand_id?._id) || <Tag>Not in Favourites</Tag>}
                  </div>
                </div>
                <div className="brand-info-item">
                  <div className="brand-info-label">Brand Detail</div>
                  <div className="brand-info-value">
                    <Link to={`/brand/view/${brand?.brand_id?._id}`} target="_blank">
                      <LinkOutlined /> {brand?.brand_id?.name}
                    </Link>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {brand?.psl_list?.length > 0 ? (
          <div style={{ marginTop: '20px' }}>
            <Collapse accordion className="service-layer-collapse">
              {brand.psl_list.map(renderServiceLayer)}
            </Collapse>
          </div>
        ) : (
          <Card style={{ marginTop: '20px' }}>
            <Empty description="No Service Layers" className="empty-state" />
          </Card>
        )}
      </div>
    );

    const brandTabs = userBrands?.map((brand) => ({
      key: brand._id,
      label: (
        <div className="brand-tab-label">
          {brand?.brand_id?.logo && (
            <Image
              src={brand.brand_id.logo}
              preview={false}
              width={28}
              height={28}
              className="brand-tab-logo"
            />
          )}
          <span className="brand-tab-name">{brand?.brand_id?.name}</span>
          {renderWishlistIndicator(brand?.brand_id?._id, false)}
        </div>
      ),
      children: renderBrandContent(brand),
    })) ?? [];

    return (
      <>
        {loading ? (
          <Skeleton active />
        ) : brandTabs?.length > 0 ? (
          <div className="brands-container">
            <Tabs
              className="brands-tabs"
              type="line"
              size="large"
              items={brandTabs}
              activeKey={activeBrandTab || brandTabs[0]?.key}
              onChange={setActiveBrandTab}
            />
          </div>
        ) : (
          <Card>
            <Empty description="No User Brands Found" className="empty-state" />
          </Card>
        )}
      </>
    );
  }

  return (
    <Card
      title={`${sectionName} Details`}
      extra={
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            Refresh
          </Button>
          <Link to={`/${routeName}${location.search}`}>
            <Button icon={<ArrowLeftOutlined />}>Back</Button>
          </Link>
        </div>
      }
    >
      {loading ? (
        <div>
          {[1, 2, 3].map((item) => (
            <Skeleton active key={item} style={{ marginBottom: 20 }} />
          ))}
        </div>
      ) : (
        <Tabs className="user-view-tabs" items={tabItems} size="large" activeKey={activeTab} onChange={setActiveTab} />
      )}
    </Card>
  );
}

export default View;
